class RootsProductTabs extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.resizeObserver = null;
    this.animationTimer = null;
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.handleButtonKeydown = this.handleButtonKeydown.bind(this);
    this.handleNavClick = this.handleNavClick.bind(this);
    this.updateNavigation = this.updateNavigation.bind(this);
  }

  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('[data-roots-product-tabs-button]'));
    this.items = Array.from(this.querySelectorAll('[data-roots-product-tabs-item]'));
    this.scroller = this.querySelector('[data-roots-product-tabs-scroller]');
    this.emptyState = this.querySelector('[data-roots-product-tabs-empty]');
    this.navButtons = Array.from(this.querySelectorAll('[data-roots-product-tabs-nav]'));
    this.viewAllLink = this.querySelector('[data-roots-product-tabs-view-all]');

    if (!this.buttons.length || !this.scroller) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.buttons.forEach((button) => {
      button.addEventListener('click', this.handleButtonClick, { signal });
      button.addEventListener('keydown', this.handleButtonKeydown, { signal });
    });

    this.navButtons.forEach((button) => {
      button.addEventListener('click', this.handleNavClick, { signal });
    });

    this.scroller.addEventListener('scroll', this.updateNavigation, { signal, passive: true });

    this.resizeObserver = new ResizeObserver(this.updateNavigation);
    this.resizeObserver.observe(this.scroller);

    this.applyFilter(this.activeTarget, false);
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    clearTimeout(this.animationTimer);
  }

  get activeTarget() {
    const activeButton = this.buttons.find((button) => button.getAttribute('aria-pressed') === 'true');
    return activeButton?.dataset.tabTarget || this.buttons[0]?.dataset.tabTarget || '_all';
  }

  handleButtonClick(event) {
    const button = event.currentTarget;
    this.activateButton(button);
  }

  handleButtonKeydown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();

    const currentIndex = this.buttons.indexOf(event.currentTarget);
    let nextIndex = currentIndex;

    if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = this.buttons.length - 1;
    } else {
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      nextIndex = (currentIndex + direction + this.buttons.length) % this.buttons.length;
    }

    const nextButton = this.buttons[nextIndex];
    nextButton.focus();
    this.activateButton(nextButton);
  }

  handleNavClick(event) {
    if (!this.scroller) return;

    const direction = event.currentTarget.dataset.rootsProductTabsNav === 'next' ? 1 : -1;
    const firstVisibleItem = this.items.find((item) => !item.hidden);
    const itemWidth = firstVisibleItem?.getBoundingClientRect().width || this.scroller.clientWidth;
    const gap = this.getCurrentGap();
    const distance = Math.max(itemWidth + gap, this.scroller.clientWidth * 0.75);

    this.scroller.scrollBy({
      left: direction * distance,
      behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  activateButton(activeButton) {
    if (!activeButton || activeButton.getAttribute('aria-pressed') === 'true') return;

    this.buttons.forEach((button) => {
      button.setAttribute('aria-pressed', String(button === activeButton));
    });

    this.applyFilter(activeButton.dataset.tabTarget || '_all', true);
  }

  applyFilter(target, shouldAnimate) {
    let visibleIndex = 0;

    this.items.forEach((item) => {
      const isVisible = target === '_all' || item.dataset.rootsProductTab === target;
      item.hidden = !isVisible;

      if (isVisible) {
        item.style.setProperty('--roots-product-tabs-index', visibleIndex);
        item.style.setProperty('--roots-product-tabs-delay', `${visibleIndex * 45}ms`);
        visibleIndex += 1;
      }
    });

    if (this.emptyState) {
      this.emptyState.hidden = visibleIndex > 0;
    }

    if (this.viewAllLink) {
      const attribute = target === '_all' ? 'data-href-all' : `data-href-${target}`;
      const href = this.viewAllLink.getAttribute(attribute) || this.viewAllLink.getAttribute('data-href-all');
      if (href) this.viewAllLink.href = href;
    }

    this.scroller.scrollTo({ left: 0, behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });

    if (shouldAnimate && !this.prefersReducedMotion()) {
      this.classList.remove('roots-product-tabs-shell--animating');
      clearTimeout(this.animationTimer);

      requestAnimationFrame(() => {
        this.classList.add('roots-product-tabs-shell--animating');
        this.animationTimer = setTimeout(() => {
          this.classList.remove('roots-product-tabs-shell--animating');
        }, 520);
      });
    }

    requestAnimationFrame(this.updateNavigation);
  }

  updateNavigation() {
    if (!this.scroller || !this.navButtons.length) return;

    const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
    const canScroll = maxScroll > 1;

    this.navButtons.forEach((button) => {
      const isPrevious = button.dataset.rootsProductTabsNav === 'previous';
      const isDisabled = !canScroll || (isPrevious ? this.scroller.scrollLeft <= 1 : this.scroller.scrollLeft >= maxScroll - 1);

      button.disabled = isDisabled;
    });
  }

  getCurrentGap() {
    const style = window.getComputedStyle(this.scroller);
    const list = this.scroller.querySelector('.roots-product-tabs__list');

    if (!list) return 0;

    return Number.parseFloat(window.getComputedStyle(list).columnGap || style.columnGap || 0) || 0;
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('roots-product-tabs')) {
  customElements.define('roots-product-tabs', RootsProductTabs);
}
